import 'regenerator-runtime/runtime';
import { call, select, takeEvery } from 'redux-saga/effects';
import {
  App,
  convertBffZuersZoneIdtoZuersZoneEnum,
  ZuersZoneEnum,
} from '@ps20-frontend/frontend-models';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  ShopTarifierungRequestEvents,
  TarifiereAngebotForShopAction,
} from '@ps20-frontend/shop-integration-redux';
import {
  createHashedTaasData,
  ShopTarifierer,
  ShopTarifierungsErgebnisHandler,
} from '@ps20-frontend/preis-lib';
import { TaasRequestModel, TaasResponseModel, TaasVhvV2Data } from '@ps20/bff-models';
import { CallEffect, ForkEffect, SelectEffect } from '@redux-saga/core/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { IAppState } from '../appState';
import { buildTaasVHVDataForShop } from './transformer/vhvData.transformer';

export const shopTarifierungsErgebnisHandler = new ShopTarifierungsErgebnisHandler<
  IAppState,
  TaasVhvV2Data
>(App.VHV_V2, getVereinbarungInfo);
export const shopTarifierer = new ShopTarifierer({
  appId: App.VHV_V2,
  taasDataBuilder: buildTaasVHVDataForShop,
  resultHandler: shopTarifierungsErgebnisHandler,
  hashFunc: createHashedTaasDataVHV,
});

export function* shopTarifierungVHV(): Generator<ForkEffect> {
  yield takeEvery(
    ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_SUCCESSFUL_VHV_V2,
    safeSaga(callSuccessOnMatchingAngebotsId, App.VHV_V2),
  );
  yield takeEvery(
    ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_SUCCESSFUL_VHV_V2,
    safeSaga(pushZuersZoneToCheckout, App.VHV_V2),
  );
  yield takeEvery(
    GlobalActions.TARIFIERE_ANGEBOT_FOR_SHOP,
    safeSaga(callTarifierungOnMatchingAngebotsId, App.VHV_V2),
  );
}

export function getVereinbarungInfo(state: IAppState): string {
  const risikoort = state.risikoort;
  const wohnflaeche = state.versObjekt.wohnflaeche;
  const risikoortString = `${risikoort.strasse} ${risikoort.hausnummer}, ${risikoort.plz} ${risikoort.ort}, `;
  const wohnflaecheString = `${wohnflaeche} m²`;
  return `${risikoortString}${wohnflaecheString}`;
}

export function* callTarifierungOnMatchingAngebotsId(
  tarifiereAngebotAction: TarifiereAngebotForShopAction,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = (yield select()) as IAppState;
  const angebotsIdIsMatching = appState.angebotsId === tarifiereAngebotAction.payload.angebotsId;

  if (angebotsIdIsMatching) {
    yield call(shopTarifierer.callTarifierung, tarifiereAngebotAction);
  }
}

export function* callSuccessOnMatchingAngebotsId(
  successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<TaasVhvV2Data>>,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = (yield select()) as IAppState;
  const originEvent: TarifiereAngebotForShopAction =
    successAction.doRequestAction.payload.originEvent;
  const angebotsIdIsMatching = appState.angebotsId === originEvent.payload.angebotsId;
  if (angebotsIdIsMatching) {
    yield call(shopTarifierer.handleSuccess, successAction);
  }
}

export function* pushZuersZoneToCheckout(
  successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<TaasVhvV2Data>>,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = (yield select()) as IAppState;
  const originEvent: TarifiereAngebotForShopAction =
    successAction.doRequestAction.payload.originEvent;
  const angebotsIdIsMatching = appState.angebotsId === originEvent.payload.angebotsId;
  const zuerszoneFromTaa = successAction.payload.gisDaten?.zuersGefaehrdungsklasse;
  if (!angebotsIdIsMatching || zuerszoneFromTaa == null) {
    return;
  }

  let zuersZone: ZuersZoneEnum;
  try {
    zuersZone = convertBffZuersZoneIdtoZuersZoneEnum(String(zuerszoneFromTaa));
  } catch (error) {
    PS20Logger.error(
      App.VHV_V2,
      error instanceof Error ? error : new Error(String(error)),
      `pushZuersZoneToCheckout: zuersGefaehrdungsklasse '${zuerszoneFromTaa}' (angebotsId=${appState.angebotsId}) cannot be mapped to ZuersZoneEnum`,
    );
    return;
  }

  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().zuersZoneReceived({
      appId: App.VHV_V2,
      angebotsId: appState.angebotsId,
      zuersZone,
    }),
  );
}

export function createHashedTaasDataVHV(taasData: TaasVhvV2Data): string {
  const relevantData = { ...taasData };
  relevantData.gisDaten = undefined;
  relevantData.plzFaktorenAusgabe = undefined;
  if (taasData.adresse) {
    relevantData.adresse = {
      plz: taasData.adresse.plz,
      ort: taasData.adresse.ort,
      strasse: taasData.adresse.strasse,
      hausnummer: taasData.adresse.hausnummer,
      latitude: taasData.adresse.latitude,
      longtitude: taasData.adresse.longtitude,
    };
  }
  return createHashedTaasData(relevantData);
}
